import { useEffect, useState } from 'react';
import {
  ACTION_LABEL,
  ACTION_TARGET_LABEL,
  type ActionLogRow,
  type ActionTarget,
} from 'shared';
import { api } from '../api';
import AdminFilters from '../AdminFilters';
import AdminSheet from '../AdminSheet';
import { alert } from '../telegram';

/**
 * Журнал действий на площадке.
 *
 * Маршрут существовал и раньше, но отдавал сотню последних записей без
 * фильтров, и экрана под него не было вовсе — журнал писался в пустоту.
 *
 * Разбор начинается не с «покажи всё», а с «что было с этой заявкой» или
 * «что делал этот человек», поэтому фильтры здесь — не украшение.
 *
 * Лента, а не таблица: у записей разная длина — у одной пусто, у другой
 * пять строк «было → стало», — и в колонках это дало бы строки высотой в
 * экран рядом с пустыми. Общее с остальными вкладками — фильтры и разбор.
 */

/** Семейства действий. Точка на конце означает «всё, что начинается с». */
const FAMILIES: Array<[label: string, value: string]> = [
  ['Всё', ''],
  ['Заявки', 'cargo.'],
  ['Отклики', 'offer.'],
  ['Рейсы', 'order.'],
  ['Файлы', 'file.'],
  ['Вход и пароли', 'auth.'],
  ['Компании', 'company.'],
  ['Участники', 'user.'],
];

const when = (iso: string) =>
  new Date(iso).toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

/** Значение «было/стало» одной строкой. Объекты сюда попасть не должны. */
function show(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (v === true) return 'да';
  if (v === false) return 'нет';
  if (Array.isArray(v)) return v.join(', ') || '—';
  if (typeof v === 'number') return v.toLocaleString('ru-RU');
  return String(v);
}

/** Кто над кем: одной строкой, как в ленте и в разборе. */
function who(r: ActionLogRow): string {
  const target = r.targetName
    ? ` → ${r.targetName}`
    : r.targetType
      ? ` → ${ACTION_TARGET_LABEL[r.targetType as ActionTarget]} ${r.targetId ?? ''}`
      : '';
  return (r.actorName ?? 'Площадка') + target;
}

export default function AdminLog() {
  const [items, setItems] = useState<ActionLogRow[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [family, setFamily] = useState('');
  const [adminOnly, setAdminOnly] = useState(false);
  const [q, setQ] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);

  async function load(more = false, id = q) {
    setLoading(true);
    try {
      const r = await api.admin.log({
        action: family || undefined,
        adminOnly: adminOnly || undefined,
        // Одно поле поиска на два фильтра: администратор копирует
        // идентификатор из соседнего экрана и не обязан знать, чей он —
        // человека или заявки. Сервер разберётся, у какой записи совпало.
        targetId: id.trim() || undefined,
        cursor: more ? (cursor ?? undefined) : undefined,
      });
      setItems((prev) => (more ? [...prev, ...r.items] : r.items));
      setCursor(r.nextCursor);
    } catch (e) {
      await alert((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [family, adminOnly]);

  const open = items.find((r) => r.id === expanded) ?? null;

  return (
    <>
      <AdminFilters
        active={(family ? 1 : 0) + (adminOnly ? 1 : 0)}
        search={
          <input
            value={q}
            placeholder="Идентификатор заявки, рейса или участника"
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void load();
            }}
          />
        }
      >
        {FAMILIES.map(([label, value]) => (
          <button
            key={value}
            type="button"
            className={family === value ? 'chip chip--on' : 'chip'}
            onClick={() => setFamily(value)}
          >
            {label}
          </button>
        ))}
        <button
          type="button"
          className={adminOnly ? 'chip chip--on' : 'chip'}
          onClick={() => setAdminOnly((v) => !v)}
        >
          Только администраторы
        </button>
      </AdminFilters>

      {loading && items.length === 0 && <div className="empty">Загрузка…</div>}
      {!loading && items.length === 0 && (
        <div className="empty">По этим условиям записей нет.</div>
      )}

      <div className="log">
        {items.map((r) => (
          /*
           * Запись открывается целиком, как строка в остальных вкладках:
           * в ленте время сокращено до «4 сен, 14:05», а идентификаторы
           * не показаны вовсе — а именно их отсюда и копируют, чтобы
           * посмотреть всё по тому же объекту.
           */
          <div
            key={r.id}
            className="log__row log__row--tap"
            role="button"
            tabIndex={0}
            onClick={() => setExpanded(r.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setExpanded(r.id);
              }
            }}
          >
            <div className="log__when meta tabular">{when(r.createdAt)}</div>

            <div className="log__what">
              <div className="log__head">
                <strong>{ACTION_LABEL[r.action] ?? r.action}</strong>
                {r.actorIsAdmin && <span className="badge badge--warn">админ</span>}
              </div>

              <div className="meta">{who(r)}</div>

              {r.details && <div className="meta">{r.details}</div>}

              {/* «Было → стало» — то, ради чего журнал и заводился: спор
                  «мы договаривались на другую сумму» разбирается этой
                  строкой, а не перепиской. */}
              {r.changes &&
                Object.entries(r.changes).map(([field, v]) => (
                  <div key={field} className="log__diff">
                    <span className="meta">{field}</span>
                    <span className="log__from">{show(v.from)}</span>
                    <span className="meta">→</span>
                    <strong>{show(v.to)}</strong>
                  </div>
                ))}
            </div>
          </div>
        ))}
      </div>

      {cursor && (
        <button className="secondary" disabled={loading} onClick={() => void load(true)}>
          Показать ещё
        </button>
      )}

      <AdminSheet
        open={open !== null}
        onClose={() => setExpanded(null)}
        title={open ? (ACTION_LABEL[open.action] ?? open.action) : ''}
        subtitle={open && who(open)}
        actions={
          open && (
            <>
              <button className="secondary" onClick={() => setExpanded(null)}>
                Закрыть
              </button>
              {/* Ради этого запись и открывают: перейти от одного действия
                  ко всей истории объекта, не переписывая идентификатор
                  руками. */}
              {open.targetId && (
                <button
                  onClick={() => {
                    const id = open.targetId!;
                    setQ(id);
                    setExpanded(null);
                    void load(false, id);
                  }}
                >
                  Всё по этому объекту
                </button>
              )}
            </>
          )
        }
      >
        {open && (
          <>
            <div className="row">
              <span className="meta">Когда</span>
              <span className="tabular">
                {new Date(open.createdAt).toLocaleString('ru-RU')}
              </span>
            </div>
            <div className="row">
              <span className="meta">Кто</span>
              <span>
                {open.actorName ?? 'Площадка'}
                {open.actorIsAdmin && <span className="badge badge--warn">админ</span>}
              </span>
            </div>
            {open.targetType && (
              <div className="row">
                <span className="meta">Над чем</span>
                <span>
                  {ACTION_TARGET_LABEL[open.targetType as ActionTarget]}
                  {open.targetName ? ` · ${open.targetName}` : ''}
                </span>
              </div>
            )}
            {open.targetId && (
              <div className="row">
                <span className="meta">Идентификатор</span>
                {/* Полностью, а не хвостом: его копируют в поиск. */}
                <span className="tabular" style={{ overflowWrap: 'anywhere' }}>
                  {open.targetId}
                </span>
              </div>
            )}
            <div className="row">
              <span className="meta">Действие</span>
              <span className="tabular">{open.action}</span>
            </div>

            {open.details && <div className="meta">{open.details}</div>}

            {open.changes && Object.keys(open.changes).length > 0 && (
              <>
                <div className="divider" />
                {Object.entries(open.changes).map(([field, v]) => (
                  <div key={field} className="log__diff">
                    <span className="meta">{field}</span>
                    <span className="log__from">{show(v.from)}</span>
                    <span className="meta">→</span>
                    <strong>{show(v.to)}</strong>
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </AdminSheet>
    </>
  );
}
